import React from 'react';
import { useForm, Controller } from 'react-hook-form';
import { UserRole } from 'types/userRoles';
import { Select, Modal, Input, Button } from 'common/components';
import { emailRegisterOptions } from 'common/constants';
import { useInviteUser } from 'services/hooks/mutations';
import { InviteUserModalProps, InviteUserForm } from './InviteUserModal.types';

const InviteUserModal: React.FC<InviteUserModalProps> = ({
    isOpen,
    onClose,
    onSuccess,
    onError,
    hasTenantAccess,
    tenants,
    selectedTenant,
    selectedUser,
    currentUserTenantId,
}) => {
    const {
        register,
        handleSubmit: handleFormSubmit,
        control,
        reset,
        formState: { errors },
    } = useForm<InviteUserForm>({
        defaultValues: {
            firstName: '',
            lastName: '',
            email: '',
            roles: [],
            tenantId: selectedTenant?.id || '',
        },
    });

    const { inviteUser, isPending } = useInviteUser();

    const onSubmit = async (data: InviteUserForm) => {
        if (hasTenantAccess && !selectedTenant && !data.tenantId) {
            onError('Please select a tenant');
            return;
        }

        const userData: Record<string, unknown> = {
            first_name: data.firstName,
            last_name: data.lastName,
            email: data.email.trim().toLowerCase(),
            roles: data.roles,
            surface: 'platform',
        };

        if (data.tenantId) {
            userData.tenant_id = data.tenantId;
        } else if (selectedTenant?.id) {
            userData.tenant_id = selectedTenant.id;
        } else if (!hasTenantAccess && currentUserTenantId) {
            userData.tenant_id = currentUserTenantId;
        }

        try {
            const response = await inviteUser(userData as any);

            const createdUser = {
                id: response.id,
                firstName: response.first_name,
                lastName: response.last_name,
                email: response.email,
                tenantId: response.tenant_id,
                roles: response.roles,
                createdAt: response.created_at,
                updatedAt: response.updated_at,
                cognitoId: response.cognito_id,
                edges: response.edges,
                inviteStatus: response.invite_status || 'pending',
            };

            const wasAddedToDifferentTenant = Boolean(
                data.tenantId && (!selectedTenant || data.tenantId !== selectedTenant.id)
            );

            onClose();
            await onSuccess(createdUser, wasAddedToDifferentTenant);
        } catch (error: any) {
            console.error('Error inviting user:', error);
            const apiMessage = error.response?.data?.message || error.response?.data?.error || error.message || '';
            const lower = String(apiMessage).toLowerCase();
            if (lower.includes('soft-deleted')) {
                onError('A soft-deleted user already exists with this email. Restore them manually before inviting.');
            } else if (lower.includes('already exists') || lower.includes('usernameexistsexception')) {
                onError('A user with this email already exists or has a pending invitation. Invite cannot be resent.');
            } else if (apiMessage) {
                onError(apiMessage);
            } else {
                onError('Failed to invite user. Please try again.');
            }
        }
    };

    const handleClose = () => {
        reset();
        onClose();
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={handleClose}
            title={hasTenantAccess && selectedTenant ? `Invite User to ${selectedTenant.name}` : 'Invite User'}
            size='md'
            showDefaultFooter={false}
        >
            <form onSubmit={handleFormSubmit(onSubmit)} id='invite-user-form' className='flex flex-col gap-md'>
                <Input
                    label='First Name'
                    type='text'
                    error={!!errors.firstName}
                    errorText={errors.firstName?.message}
                    {...register('firstName', { required: 'First name is required' })}
                />
                <Input
                    label='Last Name'
                    type='text'
                    error={!!errors.lastName}
                    errorText={errors.lastName?.message}
                    {...register('lastName', { required: 'Last name is required' })}
                />
                <Input
                    label='Email'
                    type='email'
                    error={!!errors.email}
                    errorText={errors.email?.message}
                    {...register('email', emailRegisterOptions)}
                />
                <p className='text-sm text-gray-600'>
                    An invitation email will be sent via Cognito. The user sets their own password.
                </p>
                {hasTenantAccess && (
                    <Controller
                        name='tenantId'
                        control={control}
                        rules={{ required: 'Tenant is required' }}
                        render={({ field }) => (
                            <Select
                                label='Tenant'
                                value={field.value}
                                onChange={(e) => field.onChange(e.target.value)}
                                error={!!errors.tenantId}
                                errorText={errors.tenantId?.message}
                                options={
                                    selectedTenant
                                        ? [
                                              { value: selectedTenant.id, label: selectedTenant.name },
                                              ...tenants
                                                  .filter((t) => t.id !== selectedTenant.id)
                                                  .map((tenant) => ({ value: tenant.id, label: tenant.name })),
                                          ]
                                        : [
                                              { value: '', label: 'Select a Tenant', disabled: true },
                                              ...tenants.map((tenant) => ({ value: tenant.id, label: tenant.name })),
                                          ]
                                }
                            />
                        )}
                    />
                )}
                <Controller
                    name='roles'
                    control={control}
                    rules={{ validate: (v) => v.length > 0 || 'At least one role is required' }}
                    render={({ field }) => (
                        <Select
                            label='Roles'
                            isMulti
                            value={field.value}
                            onChange={field.onChange}
                            error={!!errors.roles}
                            errorText={errors.roles?.message}
                            placeholder='Select roles...'
                            options={[
                                { value: UserRole.AGENT, label: 'Agent' },
                                { value: UserRole.MANAGER, label: 'Manager' },
                                { value: UserRole.ADMIN, label: 'Admin' },
                                ...(selectedUser && selectedUser.roles.includes(UserRole.SUPER_ADMIN)
                                    ? [{ value: UserRole.SUPER_ADMIN, label: 'Super Admin' }]
                                    : []),
                            ]}
                        />
                    )}
                />

                <div className='flex justify-end gap-md mt-xl'>
                    <Button onClick={handleClose} variant='secondary'>
                        Cancel
                    </Button>
                    <Button type='submit' disabled={isPending}>
                        Invite User
                    </Button>
                </div>
            </form>
        </Modal>
    );
};

export default InviteUserModal;
